import React, { Fragment, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { ConnectAddon } from '@confluence/connect-addon';
import {
	ContentBlogUnifiedQueryArtifact,
	ContentUnifiedQueryArtifact,
	type ContentBlogUnifiedQueryType,
	type ContentBlogUnifiedQueryVariablesType,
	type ContentUnifiedQueryType,
	type ContentUnifiedQueryVariablesType,
} from '@confluence/content-unified-query';
// eslint-disable-next-line no-restricted-imports -- deprecated - use RouteState instead
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import {
	usePageContentId,
	usePageSpaceKey,
	useContentType,
	usePageState,
} from '@confluence/page-context';
import { SPACE_OVERVIEW } from '@confluence/named-routes/entry-points/routes.ts';
import { ContentScreenBackgroundLayer } from '@confluence/content-container/entry-points/ContentScreenBackgroundLayer.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ContentViewedEvent } from '@confluence/content-body/entry-points/ContentViewedEvent.ts';
import { expVal } from '@confluence/feature-experiments';

import { ThemeExperienceTrackerSupport } from './ThemeExperienceTrackerSupport.ts';
import { useExperimentalThemeSpaceHomeOverrideQuery } from './useExperimentalThemeSpaceHomeOverrideQuery.ts';
const connectAddonContainerStyle = { position: 'relative' };

const connectAddonProps = {
	contentStyle: {
		width: '100%',
		textAlign: 'center',
		display: 'inline-block',
	},
	width: '100%',
	height: '100%',
};

const SPACE_THEME_KEY_MODULE_KEY_REGEX = /^ac:(.*)__(.*)-remote-theme$/;
export const parseThemeKey = (themeKey: string): string[] | null[] => {
	const regexResult = SPACE_THEME_KEY_MODULE_KEY_REGEX.exec(themeKey);
	return regexResult ? [regexResult[1], regexResult[2]] : [null, null];
};

export const SpaceBaseThemedComponent = ({
	children,
}: React.PropsWithChildren<{}>): React.JSX.Element | null => {
	const { match } = useContext(RoutesContext);
	const [{ contentIdLoading }] = usePageState();
	const [contentId] = usePageContentId();
	const [spaceKey] = usePageSpaceKey();
	const [contentType] = useContentType();

	const isBlogpost = contentType === 'blogpost';
	const isSpaceOverview = match?.name === SPACE_OVERVIEW.name;
	const skipContentQuery = !contentId || !spaceKey || !isSpaceOverview || contentIdLoading;
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSR = Boolean(process.env.REACT_SSR || window.__SSR_RENDERED__);

	const pageContentQueryResult = useQueryInterop<
		ContentUnifiedQueryType,
		ContentUnifiedQueryVariablesType
	>(ContentUnifiedQueryArtifact, {
		context: { single: true },
		variables: {
			contentId: contentId ?? '',
			spaceKey,
			versionOverride: null,
			isSSR,
			useNewContentTopper: true,
		},
		skip: skipContentQuery || isBlogpost,
	});

	const blogContentQueryResult = useQueryInterop<
		ContentBlogUnifiedQueryType,
		ContentBlogUnifiedQueryVariablesType
	>(ContentBlogUnifiedQueryArtifact, {
		context: { single: true },
		variables: {
			contentId: contentId ?? '',
			spaceKey,
			isSSR,
			versionOverride: null,
			useNewContentTopper: true,
		},
		skip: skipContentQuery || !isBlogpost,
	});

	const contentQueryResult = isBlogpost ? blogContentQueryResult : pageContentQueryResult;

	const themeKey = contentQueryResult?.data?.space?.theme?.themeKey;
	const routeOverrideEnabled = contentQueryResult?.data?.space?.settings?.routeOverrideEnabled;
	const [themeAddonKey, themeModuleKey] = parseThemeKey(themeKey ?? '');
	const skipOverrideQuery = !themeAddonKey || !themeModuleKey || !routeOverrideEnabled;

	const overrideQueryResult = useExperimentalThemeSpaceHomeOverrideQuery({
		variables: {
			themeAddonKey,
			themeModuleKey,
			spaceKey: spaceKey || '',
			spaceHomePageId: contentId || '',
		},
		skip: skipOverrideQuery,
	});

	const productCtx =
		overrideQueryResult.data?.experimentalThemeSpaceHomeOverride?.spaceHomeOverrideDescriptor
			?.productCtx;
	const productContext = useMemo(
		() => (productCtx ? JSON.parse(productCtx) : undefined),
		[productCtx],
	);

	if (contentQueryResult.error) {
		getMonitoringClient().submitError(contentQueryResult.error, {
			attribution: Attribution.ECOSYSTEM,
		});
		markErrorAsHandled(contentQueryResult.error);
		return <Fragment>{children}</Fragment>;
	}

	if (overrideQueryResult.error) {
		getMonitoringClient().submitError(overrideQueryResult.error, {
			attribution: Attribution.ECOSYSTEM,
		});
		markErrorAsHandled(overrideQueryResult.error);
		return <Fragment>{children}</Fragment>;
	}

	if (contentIdLoading) {
		return null;
	}

	if (skipContentQuery) {
		return <Fragment>{children}</Fragment>;
	}

	if (contentQueryResult.loading || (!skipOverrideQuery && overrideQueryResult.loading)) {
		return null;
	}

	if (skipOverrideQuery) {
		return <Fragment>{children}</Fragment>;
	}

	if (!contentQueryResult.data || !overrideQueryResult.data) {
		return <Fragment>{children}</Fragment>;
	}

	const { hasSpaceHomeOverride, spaceHomeOverrideDescriptor } =
		overrideQueryResult.data.experimentalThemeSpaceHomeOverride;

	if (!hasSpaceHomeOverride || !spaceHomeOverrideDescriptor) {
		return <Fragment>{children}</Fragment>;
	}

	// Ensure required fields are present for ConnectAddonDescriptor
	if (
		!spaceHomeOverrideDescriptor.addon_key ||
		!spaceHomeOverrideDescriptor.key ||
		!spaceHomeOverrideDescriptor.url ||
		!spaceHomeOverrideDescriptor.contextJwt ||
		!spaceHomeOverrideDescriptor.structuredContext ||
		!spaceHomeOverrideDescriptor.productCtx
	) {
		return <Fragment>{children}</Fragment>;
	}

	const descriptor = {
		addon_key: spaceHomeOverrideDescriptor.addon_key,
		key: spaceHomeOverrideDescriptor.key,
		url: spaceHomeOverrideDescriptor.url,
		contextJwt: spaceHomeOverrideDescriptor.contextJwt,
		structuredContext: spaceHomeOverrideDescriptor.structuredContext,
		productContext,
	};

	// When the metric flag is off, preserve the original render tree exactly: a
	// Fragment containing the background layer and a plain ConnectAddon (no
	// experience tracking, no iframe lifecycle callbacks).
	if (!expVal('confluence_enable_connect_group_6_metrics', 'isEnabled', false)) {
		return (
			<Fragment>
				<ContentScreenBackgroundLayer lookAndFeel={contentQueryResult?.data?.space?.lookAndFeel} />
				<ConnectAddon
					containerStyle={connectAddonContainerStyle}
					descriptor={descriptor}
					{...connectAddonProps}
				/>
				{/* eslint-disable-next-line check-react-ssr-usage/no-react-ssr */}
				{!process.env.REACT_SSR && isSpaceOverview && <ContentViewedEvent contentId={contentId} />}
			</Fragment>
		);
	}

	// When the flag is on, route through ConnectThemeRenderer which tracks the
	// CONNECT_THEME_RENDER_EXPERIENCE via the Connect iframe lifecycle callbacks.
	return (
		<ConnectThemeRenderer
			descriptor={descriptor}
			addonKey={themeAddonKey}
			moduleKey={themeModuleKey}
		>
			<ContentScreenBackgroundLayer lookAndFeel={contentQueryResult?.data?.space?.lookAndFeel} />
			{/* eslint-disable-next-line check-react-ssr-usage/no-react-ssr */}
			{!process.env.REACT_SSR && isSpaceOverview && <ContentViewedEvent contentId={contentId} />}
		</ConnectThemeRenderer>
	);
};

type ConnectThemeRendererProps = React.PropsWithChildren<{
	descriptor: React.ComponentProps<typeof ConnectAddon>['descriptor'];
	addonKey: string | null;
	moduleKey: string | null;
}>;

/**
 * Renders the remote theme via ConnectAddon and tracks the
 * CONNECT_THEME_RENDER_EXPERIENCE for the "Connect themes" capability (Group 6 of
 * the @ecosystem Feature Test Migration), gated behind the
 * `confluence_enable_connect_group_6_metrics` experiment.
 *
 * Mirrors the canonical ConnectCustomContentIframe lifecycle: start on render,
 * succeed/fail via the Connect iframe callbacks, and abort on unmount if the
 * experience started but never resolved.
 */
const ConnectThemeRenderer = ({
	children,
	descriptor,
	addonKey,
	moduleKey,
}: ConnectThemeRendererProps) => {
	const experienceStartedRef = useRef(false);
	const experienceResolvedRef = useRef(false);

	// Note: this component is only rendered when the
	// `confluence_enable_connect_group_6_metrics` flag is on (gated at the call
	// site in SpaceBaseThemedComponent), so no flag check is needed here.
	const startExperienceIfNeeded = useCallback(() => {
		const extension = { addonKey, moduleKey };

		if (!ThemeExperienceTrackerSupport.isTrackable(extension)) {
			return false;
		}

		if (!experienceStartedRef.current) {
			ThemeExperienceTrackerSupport.start(extension);
			experienceStartedRef.current = true;
		}

		return true;
	}, [addonKey, moduleKey]);

	const onIframeEstablished = useCallback(() => {
		if (startExperienceIfNeeded() && !experienceResolvedRef.current) {
			ThemeExperienceTrackerSupport.succeed();
			experienceResolvedRef.current = true;
		}
	}, [startExperienceIfNeeded]);

	const onIframeTimeout = useCallback(() => {
		if (startExperienceIfNeeded() && !experienceResolvedRef.current) {
			ThemeExperienceTrackerSupport.fail('Connect theme iframe timeout');
			experienceResolvedRef.current = true;
		}
	}, [startExperienceIfNeeded]);

	const onIframeFailedToLoad = useCallback(() => {
		if (startExperienceIfNeeded() && !experienceResolvedRef.current) {
			ThemeExperienceTrackerSupport.fail('Connect theme iframe failed to load');
			experienceResolvedRef.current = true;
		}
	}, [startExperienceIfNeeded]);

	// Start the experience as soon as we render the theme.
	useEffect(() => {
		startExperienceIfNeeded();
	}, [startExperienceIfNeeded]);

	// Abort if the component unmounts before the render experience resolves.
	useEffect(() => {
		return () => {
			if (experienceStartedRef.current && !experienceResolvedRef.current) {
				ThemeExperienceTrackerSupport.abort(
					'SpaceBaseThemedComponent unmounted before theme render experience resolved',
				);
			}
		};
	}, []);

	return (
		<Fragment>
			{children}
			<ConnectAddon
				containerStyle={connectAddonContainerStyle}
				descriptor={descriptor}
				onIframeEstablished={onIframeEstablished}
				onIframeTimeout={onIframeTimeout}
				onIframeFailedToLoad={onIframeFailedToLoad}
				{...connectAddonProps}
			/>
		</Fragment>
	);
};
